import React from 'react';
import { Container } from 'react-bootstrap';
import { Github, ArrowUpRight, ArrowRight, ArrowUp } from 'react-bootstrap-icons';
import { HashLink } from 'react-router-hash-link';
import { projects } from '../../data/projects';
import { ProjectGallery } from '../ProjectGallery';
import { SectionNav } from '../SectionNav';
import './InteriorPages.css';
import './Projects.css';

const featured = projects.filter(project => project.featured);
const sections = [...featured.map(({ id, title }) => ({ id, title })), { id: 'earlier-projects', title: 'Earlier projects' }];

const projectContexts = {
  oracle: { category: 'Browser extension', platform: 'GitHub · Python backend', flow: ['Select code', 'Retrieve context', 'Stream explanation'] },
  'process-monitor': { category: 'System utility', platform: 'macOS · Terminal', flow: ['Sample counters', 'Calculate usage', 'Redraw terminal'] },
  raiinet: { category: 'Board game', platform: 'C++ · Terminal & X11' },
};

export const Projects = () => (
  <section className="page-section projects-page">
    <Container>
      <header className="page-heading" id="project-list">
        <h1>Projects</h1>
        <p>Here's what I've been building: a GitHub code explainer, a Mac system monitor, and a board game. I've included how they work and a few rough edges.</p>
      </header>
      <SectionNav items={sections} label="Projects on this page" className="project-index" />
      <div className="project-studies">{featured.map((project, projectIndex) => {
        const context = projectContexts[project.id];
        const nextProject = featured[projectIndex + 1];
        return (
          <article className="project-study" id={project.id} key={project.id} aria-labelledby={`${project.id}-title`}>
            <header className="study-heading">
              <p className="study-category">{context?.category}</p>
              <div className="study-title-row">
                <h2 id={`${project.id}-title`}>{project.title}</h2>
                <a className="text-link study-source" href={project.github} target="_blank" rel="noreferrer"><Github aria-hidden="true" />{project.sourceLabel || 'Source on GitHub'} <ArrowUpRight aria-hidden="true" /></a>
              </div>
              <p className="study-description">{project.description}</p>
              <div className="study-meta"><ul className="study-stack" aria-label={`${project.title} technologies`}>{project.tags.map(tag => <li key={tag}>{tag}</li>)}</ul></div>
            </header>
            <div className="study-body">
              <div className="study-visual">
                <ProjectGallery project={project} />
                <p className="study-platform">{context?.platform}</p>
              </div>
              <div className="study-copy">
                {context?.flow && <div className="study-flow"><p>How it works</p><ol aria-label={`How ${project.title} works`}>{context.flow.map((step, index) => <li key={step}><span>{step}</span>{index < context.flow.length - 1 && <ArrowRight aria-hidden="true" />}</li>)}</ol></div>}
                {project.notes.map((note, index) => <div className={`project-note${index === 0 ? ' project-note-key' : ''}`} key={note.title}><h3>{note.title}</h3><p>{note.text}</p></div>)}
              </div>
            </div>
            <nav className="study-navigation" aria-label={`${project.title} navigation`}>
              <HashLink className="text-link study-back-link" to="#project-list"><ArrowUp aria-hidden="true" />Back to project list</HashLink>
              <HashLink className="text-link study-next-link" to={nextProject ? `#${nextProject.id}` : '#earlier-projects'}>
                <span>{nextProject ? `Next project: ${nextProject.title}` : 'Explore earlier projects'}</span><ArrowRight aria-hidden="true" />
              </HashLink>
            </nav>
          </article>
        );
      })}</div>
      <section className="earlier-projects" id="earlier-projects" aria-labelledby="earlier-heading">
        <div className="archive-heading"><h2 id="earlier-heading">Earlier projects</h2><p>A few more things I've built along the way.</p></div>
        <ul className="project-archive">{projects.filter(project => !project.featured).map(project => (
          <li key={project.id}>
            <div className="archive-description"><h3>{project.title}</h3><p>{project.description}</p><p className="archive-stack"><span className="visually-hidden">Built with </span>{project.tags.join(' · ')}</p></div>
            <div className="archive-links"><a href={project.github} target="_blank" rel="noreferrer" aria-label={`${project.title} on GitHub`}><Github aria-hidden="true" /> GitHub <ArrowUpRight aria-hidden="true" /></a>{project.demo && <a href={project.demo} target="_blank" rel="noreferrer" aria-label={`${project.title} live demo`}>Live demo <ArrowUpRight aria-hidden="true" /></a>}</div>
          </li>
        ))}</ul>
      </section>
    </Container>
  </section>
);
